import { PERMISSIONS } from '@/lib/permissions'

/**
 * The teller area's route components, and the tree they belong in.
 *
 * The components are named exports so the router can import them without reading this directory,
 * and `tellerRoutes` describes the paths, components, and gating permissions so they can be
 * registered without guesswork.
 *
 * Two notes for whoever wires them.
 *
 * The paths in `tellerRoutes` are the ones in `src/features/teller/navigation.ts`, character for
 * character. Until they are registered, `Link` and `useNavigate` cannot type them, so the area
 * navigates through the two thin helpers in that file; once these routes exist, those helpers can
 * be swapped for the typed hooks with no change at any call site.
 *
 * `searchSchema` names the zod schema in `./search` that each route should be given as its
 * `validateSearch`. The screens parse their own search independently of that, so a route registered
 * without one still behaves — it simply stops rejecting nonsense in the address bar early.
 */

export { TellerCounterPage } from './counter'
export { TellerCashPositionPage } from './cash-position'
export { TellerSessionListPage } from './session-list'
export { TellerSessionDetailPage } from './session-detail'
export { TillDetailPage } from './till-detail'
export { TellerSetupPage } from './setup'
export { VerificationOverridesPage } from './verification-overrides'

export {
  DEFAULT_PAGE_SIZE,
  tellerCashPositionSearchSchema,
  tellerCounterSearchSchema,
  tellerSessionDetailSearchSchema,
  tellerSessionListSearchSchema,
  tillDetailSearchSchema,
} from './search'

export type {
  CounterRailId,
  SessionDetailTab,
  TellerCashPositionSearch,
  TellerCounterSearch,
  TellerSessionDetailSearch,
  TellerSessionListSearch,
  TillDetailSearch,
} from './search'

/**
 * `TellerPage` is the counter under the name `src/router.tsx` currently imports, so the existing
 * `/teller` route keeps compiling until the tree below is wired. It is an alias, not a second
 * screen — remove it once `/teller` points at `TellerCounterPage` by name.
 */
export { TellerCounterPage as TellerPage } from './counter'

export interface TellerRouteDefinition {
  path: string
  componentName: string
  /** The permission the screen is gated on for presentation. The server re-checks everything. */
  permission: string
  /** The zod schema in `./search` to register as `validateSearch`, where the route takes one. */
  searchSchema?: string
}

export const tellerRoutes: TellerRouteDefinition[] = [
  {
    path: '/teller',
    componentName: 'TellerCounterPage',
    // Finding your own open shift reads `GET /teller/sessions`, which is gated on this. The posting
    // rails need the savings and loans codes as well, and gate themselves individually.
    permission: PERMISSIONS.teller.tillView,
    searchSchema: 'tellerCounterSearchSchema',
  },
  {
    path: '/teller/cash',
    componentName: 'TellerCashPositionPage',
    permission: PERMISSIONS.teller.tillView,
    searchSchema: 'tellerCashPositionSearchSchema',
  },
  {
    path: '/teller/sessions',
    componentName: 'TellerSessionListPage',
    permission: PERMISSIONS.teller.tillView,
    searchSchema: 'tellerSessionListSearchSchema',
  },
  {
    path: '/teller/sessions/$sessionId',
    componentName: 'TellerSessionDetailPage',
    permission: PERMISSIONS.teller.tillView,
    searchSchema: 'tellerSessionDetailSearchSchema',
  },
  {
    path: '/teller/tills/$tillId',
    componentName: 'TillDetailPage',
    permission: PERMISSIONS.teller.tillView,
    searchSchema: 'tillDetailSearchSchema',
  },
  {
    path: '/teller/setup',
    componentName: 'TellerSetupPage',
    // A till and a vault are branch infrastructure, so the code is an `admin.*` one by design.
    permission: PERMISSIONS.admin.branchManage,
  },
  {
    path: '/teller/verification-overrides',
    componentName: 'VerificationOverridesPage',
    // Not in the generated catalogue — see `features/customers/permissions.ts`. The page gates
    // itself on it rather than the route, the same way `/approvals` gates itself.
    permission: 'teller.verification.review',
  },
]
